import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { downloadCentralTechLogXlsx } from './techlog/exportTechLog';
import { supabase } from './supabaseClient';
import { useSession } from './SessionContext';
import AircraftTechLog from './AircraftTechLog';
import { defaultAppSettings, loadAppSettings } from './appSettings';
import { fetchOfficialTechLogsForBookings, indexOfficialTechLogs, saveOfficialTechLogEntry } from './officialTechLog';
import MobileAircraftTechLog from './techlog/MobileAircraftTechLog';
import { resolveTechLogRole } from './techlog/techLogRole';
import { MEDIA_QUERIES, useMediaQuery } from './responsive';
import { getAircraftAnnualCheckDate, setAircraftAnnualCheckDate } from './aircraftAnnualCheckStore';
import { bookingUpdateContainsId } from './booking/bookingUtils';

const buttonStyle = {
  background: '#1ecfff',
  color: '#181a2a',
  border: 'none',
  borderRadius: 8,
  padding: '0.5rem 1.2rem',
  fontSize: '1rem',
  fontWeight: 600,
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(30,207,255,0.10)'
};

const spreadsheetHeaderCellStyle = {
  border: '1px solid #1f2937',
  color: '#000000',
  background: '#f2f4f7',
  fontSize: '0.76rem',
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
  textAlign: 'left',
  padding: '0.38rem 0.35rem',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  lineHeight: 1.15
};

const spreadsheetCellStyle = {
  border: '1px solid #1f2937',
  padding: '0.3rem 0.35rem',
  verticalAlign: 'top',
  color: '#000000',
  fontSize: '0.83rem'
};

const spreadsheetInputStyle = {
  width: '100%',
  minWidth: 0,
  padding: '0.18rem 0.22rem',
  borderRadius: 0,
  border: 'none',
  background: 'transparent',
  color: '#000000',
  boxShadow: 'none',
  outline: 'none',
  fontSize: '0.8rem',
  boxSizing: 'border-box'
};

function isOwnerByEmail(email) {
  const normalized = String(email || '').trim().toLowerCase();
  return normalized === 'owner@demo.wingit.app';
}

function sanitizeTechLogPrefill(entry = {}) {
  return entry && typeof entry === 'object'
    ? entry
    : {};
}

function hasMeaningfulValue(value) {
  if (value === undefined || value === null) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim() !== '';
  }

  return true;
}

function firstMeaningfulValue(...values) {
  for (const value of values) {
    if (hasMeaningfulValue(value)) {
      return value;
    }
  }

  return '';
}

function toUpperAirfield(value) {
  return String(value || '')
    .trim()
    .toUpperCase();
}

function inferFlightTypeFromRoute(departureAirfield, arrivalAirfield) {
  const departure = toUpperAirfield(departureAirfield);
  const arrival = toUpperAirfield(arrivalAirfield);

  if (!departure || !arrival) {
    return '';
  }

  return departure === arrival ? 'local' : 'land-away';
}

function getSubmissionRouteFields(submission = {}) {
  const legs = Array.isArray(submission?.legs)
    ? submission.legs
    : [];

  const firstLeg = legs[0] || {};
  const lastLeg =
    legs.length > 0 ? legs[legs.length - 1] : {};

  const departureAirfield = firstMeaningfulValue(
    submission?.departureAirfield,
    submission?.departure_airfield,
    firstLeg?.from,
    firstLeg?.departureAirfield,
    firstLeg?.departure_airfield
  );

  const arrivalAirfield = firstMeaningfulValue(
    submission?.arrivalAirfield,
    submission?.destinationAirfield,
    submission?.destination_airfield,
    submission?.arrival_airfield,
    lastLeg?.to,
    lastLeg?.arrivalAirfield,
    lastLeg?.destinationAirfield,
    lastLeg?.arrival_airfield,
    lastLeg?.destination_airfield
  );

  return {
    departureAirfield: toUpperAirfield(
      departureAirfield
    ),
    arrivalAirfield: toUpperAirfield(
      arrivalAirfield
    ),
  };
}

function hasUsableSubmissionRoute(submission = {}) {
  const route = getSubmissionRouteFields(
    submission
  );

  return Boolean(
    (route.departureAirfield &&
      route.arrivalAirfield) ||
      hasMeaningfulValue(
        submission?.flightType
      ) ||
      hasMeaningfulValue(
        submission?.flight_type
      )
  );
}

function mergePostFlightWithOfficial(
  booking,
  officialEntry = {}
) {
  const briefing =
    booking?.briefing_data &&
    typeof booking.briefing_data === 'object'
      ? booking.briefing_data
      : {};

  const postFlightSubmission =
    briefing?.techLogSubmission &&
    typeof briefing.techLogSubmission ===
      'object'
      ? briefing.techLogSubmission
      : {};

  const hasPostFlightSubmission =
    Boolean(
      briefing &&
        typeof briefing === 'object' &&
        briefing.techLogSubmission &&
        typeof briefing.techLogSubmission ===
          'object'
    );

  const mergedEntry = {
    ...postFlightSubmission,
  };

  Object.entries(
    officialEntry &&
      typeof officialEntry === 'object'
      ? officialEntry
      : {}
  ).forEach(([field, value]) => {
    if (hasMeaningfulValue(value)) {
      mergedEntry[field] = value;
    }
  });

  if (hasPostFlightSubmission) {
    const normalizedFuel =
      firstMeaningfulValue(
        mergedEntry?.fuelUpliftLitres,
        mergedEntry?.fuel_uplift_litres
      );
    const normalizedOil =
      firstMeaningfulValue(
        mergedEntry?.oilUpliftLitres,
        mergedEntry?.oil_uplift_litres
      );

    mergedEntry.fuelUpliftLitres = hasMeaningfulValue(
      normalizedFuel
    )
      ? String(normalizedFuel)
      : '0';

    mergedEntry.oilUpliftLitres = hasMeaningfulValue(
      normalizedOil
    )
      ? String(normalizedOil)
      : '0';
  }

  return {
    briefing,
    postFlightSubmission,
    mergedEntry,
  };
}

function buildOutstandingPrefill({
  booking,
  pilotName,
  baseAirfield,
  officialEntry,
}) {
  const {
    briefing,
    postFlightSubmission,
    mergedEntry,
  } = mergePostFlightWithOfficial(
    booking,
    officialEntry
  );

  const routeFromSubmission =
    getSubmissionRouteFields(
      postFlightSubmission
    );

  const departureAirfield = toUpperAirfield(
    firstMeaningfulValue(
      mergedEntry?.departureAirfield,
      mergedEntry?.departure_airfield,
      routeFromSubmission.departureAirfield,
      booking?.departure_airfield,
      briefing?.departureAirfield,
      briefing?.departure_airfield
    )
  );

  const arrivalAirfield = toUpperAirfield(
    firstMeaningfulValue(
      mergedEntry?.arrivalAirfield,
      mergedEntry?.destinationAirfield,
      mergedEntry?.destination_airfield,
      mergedEntry?.arrival_airfield,
      routeFromSubmission.arrivalAirfield,
      booking?.destination_airfield,
      briefing?.destinationAirfield,
      briefing?.destination_airfield
    )
  );

  const storedFlightType = String(
    firstMeaningfulValue(
      mergedEntry?.flightType,
      mergedEntry?.flight_type,
      briefing?.flightType,
      briefing?.flight_type
    )
  )
    .trim()
    .toLowerCase();

  const inferredFlightType =
    inferFlightTypeFromRoute(
      departureAirfield,
      arrivalAirfield
    );

  const resolvedFlightType =
    storedFlightType || inferredFlightType;

  const bookingRouteRecorded =
    hasUsableSubmissionRoute(
      postFlightSubmission
    ) ||
    hasUsableSubmissionRoute(mergedEntry) ||
    Boolean(departureAirfield && arrivalAirfield);

  return {
    ...sanitizeTechLogPrefill(mergedEntry),
    bookingId: String(booking?.id || ''),
    flightNumber: String(booking?.id || ''),
    flightDate: booking?.date || '',
    pilotName,
    bookingFlightType: resolvedFlightType,
    bookingRouteRecorded,
    bookingBaseAirfield: baseAirfield,
    bookingDestinationAirfield:
      arrivalAirfield || baseAirfield || '',
    bookingReturnAirfield: baseAirfield,
    departureAirfield,
    arrivalAirfield,
    departureTime: firstMeaningfulValue(
      mergedEntry?.departureTime,
      mergedEntry?.actualDepartureTime,
      booking?.time
    ),
    arrivalTime: firstMeaningfulValue(
      mergedEntry?.arrivalTime,
      mergedEntry?.actualArrivalTime,
      booking?.end_time
    ),
    flightTime: firstMeaningfulValue(
      mergedEntry?.flightTime,
      mergedEntry?.duration,
      booking?.duration
    ),
    methodStart: firstMeaningfulValue(
      mergedEntry?.methodStart,
      mergedEntry?.hobbsStart,
      mergedEntry?.startReading
    ),
    methodEnd: firstMeaningfulValue(
      mergedEntry?.methodEnd,
      mergedEntry?.hobbsEnd,
      mergedEntry?.endReading
    ),
    startReading: firstMeaningfulValue(
      mergedEntry?.startReading,
      mergedEntry?.methodStart,
      mergedEntry?.hobbsStart
    ),
    endReading: firstMeaningfulValue(
      mergedEntry?.endReading,
      mergedEntry?.methodEnd,
      mergedEntry?.hobbsEnd
    ),
    fuelUpliftLitres:
      String(
        firstMeaningfulValue(
          mergedEntry?.fuelUpliftLitres,
          mergedEntry?.fuel_uplift_litres,
          ''
        )
      ),
    oilUpliftLitres:
      String(
        firstMeaningfulValue(
          mergedEntry?.oilUpliftLitres,
          mergedEntry?.oil_uplift_litres,
          ''
        )
      ),
    aircraftStatus: firstMeaningfulValue(
      mergedEntry?.aircraftStatus,
      mergedEntry?.aircraft_status
    ),
    defect: firstMeaningfulValue(
      mergedEntry?.defect,
      mergedEntry?.snag
    ),
    snag: firstMeaningfulValue(
      mergedEntry?.snag,
      mergedEntry?.defect
    ),
    signature: firstMeaningfulValue(
      mergedEntry?.signature,
      mergedEntry?.pilotName,
      pilotName
    ),
    remarks: firstMeaningfulValue(
      mergedEntry?.remarks,
      mergedEntry?.notes
    ),
    notes: firstMeaningfulValue(
      mergedEntry?.notes,
      mergedEntry?.remarks
    ),
  };
}

function formatLocalDate(date) {
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
}

function formatLocalDateShortYear(date) {
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = String(date.getFullYear()).slice(-2);
  return `${d}/${m}/${y}`;
}

function formatDateValue(dateValue) {
  const value = String(dateValue || '').trim();
  if (!value) return '';

  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}/${String(isoMatch[1]).slice(-2)}`;
  }

  const parsed = new Date(value);
  if (Number.isFinite(parsed.getTime())) {
    return formatLocalDateShortYear(parsed);
  }

  return value;
}

function formatAirfields(departureAirfield, arrivalAirfield) {
  const dep = String(departureAirfield || '').trim().toUpperCase();
  const arr = String(arrivalAirfield || '').trim().toUpperCase();
  if (!dep && !arr) return '';
  return `${dep} / ${arr}`.trim();
}

function getAutoIncrementFlightNumber(baseFlightNumber, offset) {
  const base = String(baseFlightNumber || '').replace(/\D/g, '').trim();
  if (!base) return '';

  const next = String(Number(base) + Number(offset || 0)).padStart(base.length, '0');
  return next;
}

function parseDateTime(dateValue, timeValue) {
  if (!dateValue || !timeValue) return null;
  const parsed = new Date(`${dateValue}T${timeValue}`);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function getCheckCountdownHours(entry = {}) {
  const direct = Number(entry?.checkCountdownHours);
  if (Number.isFinite(direct)) return direct;

  const due = Number(entry?.nextCheckDueAt);
  const end = Number(entry?.methodEnd ?? entry?.hobbsEnd);
  if (Number.isFinite(due) && Number.isFinite(end)) {
    return Number((due - end).toFixed(1));
  }

  return null;
}

function formatCheckCountdownHours(entry = {}) {
  const countdownHours = getCheckCountdownHours(entry);
  if (!Number.isFinite(countdownHours)) return '';
  return Number(countdownHours).toFixed(1);
}

function getAnnualCountdownDays(dateValue) {
  if (!dateValue) return null;
  const due = new Date(`${dateValue}T00:00:00`);
  if (!Number.isFinite(due.getTime())) return null;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffMs = due.getTime() - today.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function getAnnualDueStyle(daysRemaining) {
  if (!Number.isFinite(daysRemaining)) {
    return { color: '#8db7e6', fontWeight: 700 };
  }

  if (daysRemaining < 0) {
    return { color: '#ff9f9f', fontWeight: 800, display: 'inline-block', animation: 'wingit-text-pulse-red 3.2s infinite' };
  }

  if (daysRemaining <= 30) {
    return { color: '#ff9f9f', fontWeight: 800, display: 'inline-block', animation: 'wingit-text-pulse-red 3.6s infinite' };
  }

  return { color: '#9ef4bf', fontWeight: 700 };
}

function getFiftyHourDueStyle(countdownHours, warningWindowHours = 12) {
  if (!Number.isFinite(countdownHours)) {
    return { color: '#7f99b8', fontWeight: 700 };
  }

  if (countdownHours <= 0) {
    return { color: '#ff9f9f', fontWeight: 800, display: 'inline-block', animation: 'wingit-text-pulse-red 3.2s infinite' };
  }

  if (countdownHours <= warningWindowHours) {
    return { color: '#ff9f9f', fontWeight: 800, display: 'inline-block', animation: 'wingit-text-pulse-red 3.6s infinite' };
  }

  return { color: '#9ef4bf', fontWeight: 700 };
}

function formatDecimalHours(value) {
  const rounded = Math.round(value * 100) / 100;
  return String(rounded).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
}

function computeTechLogSheetScale(width) {
  const viewport = Number(width) || 1440;
  const availableWidth = Math.min(
    Math.max(viewport - 70, 320),
    1660
  );

  return Math.min(
    1,
    availableWidth / 1920
  );
}

function getPaperLogKey(aircraftId) {
  const key = String(aircraftId || '').trim();
  if (!key) return '';
  return `paper-${key}`;
}

function buildPaperLogDraft(entry = {}) {
  const source = entry && typeof entry === 'object' ? entry : {};
  return {
    flightNumber: String(firstMeaningfulValue(source.flightNumber, '')),
    flightDate: String(firstMeaningfulValue(source.flightDate, source.date, '')),
    departureAirfield: String(firstMeaningfulValue(source.departureAirfield, '')),
    arrivalAirfield: String(firstMeaningfulValue(source.arrivalAirfield, '')),
    departureTime: String(firstMeaningfulValue(source.departureTime, '')),
    arrivalTime: String(firstMeaningfulValue(source.arrivalTime, '')),
    flightTime: String(firstMeaningfulValue(source.flightTime, '')),
    fuelUpliftLitres: String(firstMeaningfulValue(source.fuelUpliftLitres, source.fuel_uplift_litres, '')),
    oilUpliftLitres: String(firstMeaningfulValue(source.oilUpliftLitres, source.oil_uplift_litres, '')),
    signature: String(firstMeaningfulValue(source.signature, source.pilotName, '')),
    nextCheck: String(firstMeaningfulValue(source.nextCheck, '')),
    defect: String(firstMeaningfulValue(source.defect, source.snag, '')),
    rectification: String(firstMeaningfulValue(source.rectification, '')),
    engineerSignature: String(firstMeaningfulValue(source.engineerSignature, '')),
    remarks: String(firstMeaningfulValue(source.remarks, source.notes, '')),
  };
}

function buildSpreadsheetDraft({ booking, entry, aircraft, defaultMethod, defaultTaxiTimeEnabled, defaultTaxiTimeMinutes, defaultTaxiTimeHours }) {
  const source = entry && typeof entry === 'object' ? entry : {};
  const pickValue = (field, fallback = '') => {
    const sourceValue = source[field];
    if (sourceValue !== undefined && sourceValue !== null && String(sourceValue).trim() !== '') {
      return sourceValue;
    }

    return fallback;
  };
  const ownerMethod = aircraft?.recording_method || defaultMethod || 'hobbs';
  const hasSourceTaxi = source.taxiTime !== undefined && source.taxiTime !== null && String(source.taxiTime).trim() !== '';
  const parsedDefaultTaxiMinutes = Number(defaultTaxiTimeMinutes);
  const parsedDefaultTaxiHours = Number(defaultTaxiTimeHours);
  const defaultTaxiDecimal = Number.isFinite(parsedDefaultTaxiMinutes) && parsedDefaultTaxiMinutes >= 0
    ? parsedDefaultTaxiMinutes / 60
    : parsedDefaultTaxiHours;
  const shouldApplyDefaultTaxi = defaultTaxiTimeEnabled !== false && Number.isFinite(defaultTaxiDecimal) && defaultTaxiDecimal >= 0;
  const draftTaxiTime = hasSourceTaxi
    ? String(source.taxiTime)
    : shouldApplyDefaultTaxi
      ? formatDecimalHours(defaultTaxiDecimal)
      : '';

  return {
    flightNumber: pickValue('flightNumber', ''),
    departureAirfield: pickValue('departureAirfield', ''),
    arrivalAirfield: pickValue('arrivalAirfield', ''),
    departureTime: pickValue('departureTime', booking?.time || ''),
    arrivalTime: pickValue('arrivalTime', booking?.end_time || ''),
    recordingMethod: ownerMethod,
    methodStart: firstMeaningfulValue(source.methodStart, source.hobbsStart, source.startReading, ''),
    methodEnd: firstMeaningfulValue(source.methodEnd, source.hobbsEnd, source.endReading, ''),
    flightTime: pickValue('flightTime', ''),
    taxiTime: draftTaxiTime,
    defect: pickValue('defect', source.snag || ''),
    rectification: pickValue('rectification', ''),
    signature: pickValue('signature', ''),
    engineerSignature: pickValue('engineerSignature', ''),
    nextCheck: pickValue('nextCheck', Number.isFinite(Number(source.checkCountdownHours)) ? String(source.checkCountdownHours) : ''),
    fuelUpliftLitres: pickValue('fuelUpliftLitres', ''),
    oilUpliftLitres: pickValue('oilUpliftLitres', ''),
    checkCompleted: !!source.checkCompleted,
    checkType: source.checkType || '',
    checkCompletedAt: source.checkCompletedAt || '',
    notes: pickValue('notes', source.remarks || ''),
    remarks: pickValue('remarks', source.notes || ''),
  };
}

function isMissingEndColumnsError(error) {
  const message = String(error?.message || '').toLowerCase();
  return message.includes('end_date') || message.includes('end_time');
}

function isMissingAircraftMethodColumnError(error) {
  const message = String(error?.message || '').toLowerCase();
  return message.includes('recording_method') || message.includes('column');
}

function isBookingsTimeOrderError(error) {
  const code = String(error?.code || '').toUpperCase();
  const message = String(error?.message || '').toLowerCase();
  return code === 'PGRST100'
    || message.includes('failed to parse order')
    || (message.includes('order') && message.includes('time'));
}

async function runBookingsQueryWithTimeOrderFallback(buildQuery) {
  let result = await buildQuery(true);
  if (!result.error || !isBookingsTimeOrderError(result.error)) {
    return result;
  }

  result = await buildQuery(false);
  return result;
}

export async function fetchAircraftTechLogBookings(
  supabaseClient,
  aircraftIds
) {
  let includeEndColumns = true;

  const runQuery = () =>
    runBookingsQueryWithTimeOrderFallback(
      (includeTimeOrder) => {
        const optionalFields = [
          includeEndColumns ? 'end_date, end_time' : '',
        ].filter(Boolean);
        const fields = [
          'id',
          'user_id',
          'aircraft_id',
          'date',
          'time',
          'duration',
          ...optionalFields,
          'briefing_data',
          'tech_log_status',
          'tech_log_completed_at',
        ].join(', ');
        let query = supabaseClient
          .from('bookings')
          .select(fields)
          .in('aircraft_id', aircraftIds)
          .order('date', { ascending: false });

        if (includeTimeOrder) {
          query = query.order('time', { ascending: false });
        }

        return query;
      }
    );

  let result = await runQuery();
  if (result.error && isMissingEndColumnsError(result.error)) {
    includeEndColumns = false;
    result = await runQuery();
  }

  if (result.error) return result;

  return {
    ...result,
    data: (result.data || []).map((row) => ({
      ...row,
      ...(includeEndColumns
        ? {}
        : {
            end_date: null,
            end_time: null,
          }),
    })),
  };
}

export default function AircraftTechLogPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const session = useSession();
  const user = session?.user;
  const organisationId = String(
    session?.profile?.organisation_id ||
    user?.organisation_id ||
    user?.user_metadata?.organisation_id ||
    user?.app_metadata?.organisation_id ||
    ''
  ).trim();
  const selectedAircraftId = String(
  searchParams.get('aircraftId') || ''
).trim();

const selectedBookingId = String(
  searchParams.get('bookingId') || ''
).trim();

const fromQueryParam = String(
  searchParams.get('from') || ''
).trim();

const sourcePage = String(
  fromQueryParam
)
  .trim()
  .toLowerCase();
  const isPhone = useMediaQuery(MEDIA_QUERIES.phone);
  const [roleResolution, setRoleResolution] = useState({
    userId: '',
    role: '',
    resolved: false,
  });
  const currentRoleResolution =
    roleResolution.userId === String(user?.id || '').trim()
      ? roleResolution
      : null;
  const roleResolved =
    !user?.id || Boolean(currentRoleResolution?.resolved);
  const resolvedRole =
    currentRoleResolution?.role || '';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookings, setBookings] = useState([]);
  const [aircraftMap, setAircraftMap] = useState({});
  const [userMap, setUserMap] = useState({});
  const [techLogMap, setTechLogMap] = useState({});
  const [techLogVersionMap, setTechLogVersionMap] = useState({});
  const [expandedBookingId, setExpandedBookingId] = useState(null);
  const [saveNoticeMap, setSaveNoticeMap] = useState({});
  const [spreadsheetDraftMap, setSpreadsheetDraftMap] = useState({});
  const [annualCheckDate, setAnnualCheckDate] = useState('');
const [paperLogDraft, setPaperLogDraft] = useState(buildPaperLogDraft());

const [
  onBehalfReason,
  setOnBehalfReason,
] = useState('');

const [
  onBehalfSaving,
  setOnBehalfSaving,
] = useState(false);
  const [viewportWidth, setViewportWidth] =
    useState(() => window.innerWidth || 1440);
  const [desktopSheetScale, setDesktopSheetScale] =
    useState(() =>
      computeTechLogSheetScale(
        window.innerWidth || 1440
      )
    );
  const appSettings = useMemo(
    () =>
      organisationId
        ? loadAppSettings(organisationId)
        : defaultAppSettings,
    [organisationId]
  );
  const settingsBaseAirfield = String(
    appSettings?.operational?.baseAirfield || ''
  )
    .trim()
    .toUpperCase();

  const [
    databaseBaseAirfield,
    setDatabaseBaseAirfield,
  ] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadClubBaseAirfield() {
      if (!organisationId) {
        if (!cancelled) {
          setDatabaseBaseAirfield('');
        }

        return;
      }

      const { data, error: airfieldError } =
        await supabase
          .from('airfields')
          .select('icao')
          .eq('organisation_id', organisationId)
          .maybeSingle();

      if (cancelled) {
        return;
      }

      if (airfieldError) {
        console.warn(
          'Could not load club base airfield for Tech Log.',
          airfieldError
        );

        setDatabaseBaseAirfield('');
        return;
      }

      setDatabaseBaseAirfield(
        String(data?.icao || '')
          .trim()
          .toUpperCase()
      );
    }

    loadClubBaseAirfield();

    return () => {
      cancelled = true;
    };
  }, [organisationId]);

  const baseAirfield =
    databaseBaseAirfield ||
    settingsBaseAirfield;
  const useMobileTableScroll =
    viewportWidth < 1180;

  useEffect(() => {
    let cancelled = false;

    async function resolveRole() {
      if (!user?.id) {
        if (!cancelled) {
          setRoleResolution({
            userId: '',
            role: '',
            resolved: true,
          });
        }
        return;
      }

      const authUserId = String(user.id).trim();
      setRoleResolution({
        userId: authUserId,
        role: '',
        resolved: false,
      });

      const { data } = await supabase
        .from('users')
        .select('role')
        .eq('auth_id', authUserId)
        .maybeSingle();

      if (!cancelled) {
        setRoleResolution({
          userId: authUserId,
          role: resolveTechLogRole({
            databaseRole: data?.role,
            profileRole: session?.profile?.role,
            user,
          }),
          resolved: true,
        });
      }
    }

    resolveRole();
    return () => {
      cancelled = true;
    };
  }, [
    user,
    user?.id,
    user?.user_metadata?.role,
    user?.app_metadata?.role,
    user?.role,
    session?.profile?.role,
  ]);

  useEffect(() => {
    function handleResize() {
      const nextWidth =
        window.innerWidth || 1440;

      setViewportWidth(nextWidth);
      setDesktopSheetScale(
        computeTechLogSheetScale(nextWidth)
      );
    }

    window.addEventListener(
      'resize',
      handleResize
    );

    return () => {
      window.removeEventListener(
        'resize',
        handleResize
      );
    };
  }, []);

  const isOwner = useMemo(() => {
    const roles = [resolvedRole, user?.user_metadata?.role, user?.app_metadata?.role, user?.role]
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean);
    return roleResolved && (
      roles.includes('owner') || isOwnerByEmail(user?.email)
    );
  }, [
    roleResolved,
    resolvedRole,
    user?.user_metadata?.role,
    user?.app_metadata?.role,
    user?.role,
    user?.email,
  ]);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      if (!user?.id || !roleResolved) {
        return;
      }

      if (!isOwner) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      if (!organisationId) {
        setError('Could not identify the current organisation.');
        setLoading(false);
        return;
      }

      const {
        data: aircraftRowsRaw,
        error: aircraftErrorRaw,
      } = await supabase
        .from('aircraft')
        .select('id, registration, icao_type, recording_method')
        .eq('organisation_id', organisationId);

      if (cancelled) return;

      let aircraftRows = aircraftRowsRaw || [];
      if (
        aircraftErrorRaw &&
        isMissingAircraftMethodColumnError(
          aircraftErrorRaw
        )
      ) {
        const fallbackAircraft = await supabase
          .from('aircraft')
          .select('id, registration, icao_type')
          .eq('organisation_id', organisationId);
        aircraftRows = (
          fallbackAircraft.data || []
        ).map((row) => ({
          ...row,
          recording_method: 'hobbs',
        }));
      }

      const aircraftIds = aircraftRows
        .map((row) =>
          String(row.id || '').trim()
        )
        .filter(Boolean);

      const aircraftIndex = {};
      (aircraftRows || []).forEach((row) => {
        aircraftIndex[String(row.id)] = row;
      });

      if (aircraftIds.length === 0) {
        setAircraftMap(aircraftIndex);
        setUserMap({});
        setBookings([]);
        setTechLogMap({});
        setTechLogVersionMap({});
        setLoading(false);
        return;
      }

      const { data: userRows } = await supabase
        .from('users')
        .select(
          'id, auth_id, full_name, email, organisation_id'
        )
        .eq('organisation_id', organisationId);

      const bookingsResult = await fetchAircraftTechLogBookings(
        supabase,
        aircraftIds
      );

      const {
        data: bookingRows,
        error: bookingError,
      } = bookingsResult;

      if (cancelled) return;

      if (bookingError) {
        setError('Could not load flight history for tech logs.');
        setLoading(false);
        return;
      }

      const userIndex = {};
      (userRows || []).forEach((row) => {
        const rowId = String(
          row?.id || ''
        ).trim();
        const rowAuthId = String(
          row?.auth_id || ''
        ).trim();

        if (rowId) {
          userIndex[rowId] = row;
        }

        if (rowAuthId) {
          userIndex[rowAuthId] = row;
        }
      });

      const safeBookings = bookingRows || [];
      const bookingIds = safeBookings.map((row) => String(row.id));
      const paperKeys = Object.keys(aircraftIndex).map((aircraftId) => getPaperLogKey(aircraftId)).filter(Boolean);
      const { data: officialRows } = await fetchOfficialTechLogsForBookings(bookingIds);
      const { data: paperRows } = await fetchOfficialTechLogsForBookings(paperKeys);
      const indexed = indexOfficialTechLogs([...(officialRows || []), ...(paperRows || [])]);

      setAircraftMap(aircraftIndex);
      setUserMap(userIndex);
      setBookings(safeBookings);
      setTechLogMap(indexed.logMap);
      setTechLogVersionMap(indexed.versionMap);
      setLoading(false);
    }

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [
  user?.id,
  roleResolved,
  isOwner,
  organisationId,
]);

  async function handleTechLogSave(bookingId, entry) {
    const noticeKey = `${bookingId}:tech`;
    const bookingKey = String(bookingId);

    const result = await saveOfficialTechLogEntry({
      bookingId: bookingKey,
      userId: user?.id,
      entry,
      expectedVersion: techLogVersionMap[bookingKey] ?? 0,
    });

    if (result.ok) {
      setTechLogMap((prev) => ({ ...prev, [bookingKey]: entry }));
      setTechLogVersionMap((prev) => ({ ...prev, [bookingKey]: result.version }));
    }

    setSaveNoticeMap((prev) => ({
      ...prev,
      [noticeKey]: result.ok
        ? 'Official tech log saved.'
        : (result.message || 'Another user updated this log first. Reload and try again.'),
    }));

        window.setTimeout(() => {
      setSaveNoticeMap((prev) => {
        if (!prev[noticeKey]) return prev;
        const next = { ...prev };
        delete next[noticeKey];
        return next;
      });
    }, result.ok ? 2500 : 4000);

    return result;
  }
  async function handleCompleteOnBehalf(entry) {
    if (
      !selectedBooking ||
      !user?.id ||
      onBehalfSaving
    ) {
      return;
    }

    const reason = String(
      onBehalfReason || ''
    ).trim();

    const noticeKey =
      `${selectedBooking.id}:tech`;

    if (!reason) {
      setSaveNoticeMap((prev) => ({
        ...prev,
        [noticeKey]:
          'Enter a reason for completing this entry on behalf of the pilot.',
      }));

      return;
    }

    setOnBehalfSaving(true);

    const completedAt =
      new Date().toISOString();

    const ownerName =
      session?.profile?.full_name ||
      user?.user_metadata?.full_name ||
      user?.email ||
      'Club owner';

    const pilotName =
      selectedBookingPilot?.full_name ||
      selectedBookingPilot?.email ||
      selectedBooking.user_id ||
      'Unknown pilot';

    const payload = {
      ...entry,

      bookingId: String(
        selectedBooking.id
      ),

      flightNumber:
        entry?.flightNumber ||
        String(selectedBooking.id),

      flightDate:
        entry?.flightDate ||
        selectedBooking.date ||
        '',

      pilotId: String(
        selectedBooking.user_id || ''
      ),

      pilotName,

      signature:
        `${ownerName} (on behalf)`,

      completedOnBehalf: true,
      completedOnBehalfReason: reason,
      completedOnBehalfAt: completedAt,
      completedOnBehalfBy:
        String(user.id),

      completedOnBehalfByName:
        ownerName,
    };

    const saveResult =
      await handleTechLogSave(
        selectedBooking.id,
        payload
      );

    if (!saveResult?.ok) {
      setOnBehalfSaving(false);
      return;
    }

    const existingBriefing =
      selectedBooking.briefing_data &&
      typeof selectedBooking.briefing_data ===
        'object'
        ? selectedBooking.briefing_data
        : {};

    const updatedBriefing = {
      ...existingBriefing,

      techLogSubmission: payload,

      techLogCompletedOnBehalf: {
        reason,
        completedAt,
        completedBy: String(user.id),
        completedByName: ownerName,
      },
    };

    const { data: bookingUpdateRows, error: bookingUpdateError } =
      await supabase
        .from('bookings')
        .update({
          briefing_data: updatedBriefing,
          tech_log_status: 'completed',
          tech_log_completed_at:
            completedAt,
        })
        .eq(
          'id',
          selectedBooking.id
        )
        .select('id');

    if (bookingUpdateError || !bookingUpdateContainsId(bookingUpdateRows, selectedBooking.id)) {
      setSaveNoticeMap((prev) => ({
        ...prev,
        [noticeKey]:
          'The Tech Log was saved, but the booking could not be marked complete.',
      }));

      setOnBehalfSaving(false);
      return;
    }

    setBookings((prev) =>
      prev.map((row) =>
        String(row.id) ===
        String(selectedBooking.id)
          ? {
              ...row,
              briefing_data:
                updatedBriefing,
              tech_log_status:
                'completed',
              tech_log_completed_at:
                completedAt,
            }
          : row
      )
    );

    setOnBehalfReason('');

    setSaveNoticeMap((prev) => ({
      ...prev,
      [noticeKey]:
        'Post-Flight completed on behalf of the pilot.',
    }));

    setOnBehalfSaving(false);
  }
  const visibleBookings = useMemo(() => {
    if (!selectedAircraftId) return bookings;
    return bookings.filter((booking) => String(booking.aircraft_id) === selectedAircraftId);
  }, [bookings, selectedAircraftId]);

  const selectedAircraft = useMemo(() => {
  if (!selectedAircraftId) return null;

  return aircraftMap[selectedAircraftId] || null;
}, [aircraftMap, selectedAircraftId]);

const selectedBooking = useMemo(() => {
  if (!selectedBookingId) return null;

  return (
    visibleBookings.find(
      (booking) =>
        String(booking.id) === selectedBookingId
    ) || null
  );
}, [selectedBookingId, visibleBookings]);

const selectedBookingPilot = useMemo(() => {
  if (!selectedBooking) return null;

  return (
    userMap[String(selectedBooking.user_id)] ||
    null
  );
}, [selectedBooking, userMap]);

const selectedBookingEntry = useMemo(() => {
  if (!selectedBooking) return {};

  const officialEntry =
    techLogMap[String(selectedBooking.id)] ||
    {};

  return mergePostFlightWithOfficial(
    selectedBooking,
    officialEntry
  ).mergedEntry;
}, [selectedBooking, techLogMap]);
const selectedBookingHasOfficialEntry =
  useMemo(() => {
    if (!selectedBooking) return false;

    const officialEntry =
      techLogMap[
        String(selectedBooking.id)
      ];

    return Boolean(
      officialEntry &&
        typeof officialEntry === 'object' &&
        Object.keys(officialEntry).length > 0
    );
  }, [selectedBooking, techLogMap]);

const selectedBookingNeedsOnBehalfCompletion =
  Boolean(
    selectedBooking &&
      !selectedBookingHasOfficialEntry
  );
const selectedBookingPrefill = useMemo(() => {
  if (!selectedBooking) return {};

  const bookingPilotName =
    selectedBookingPilot?.full_name ||
    selectedBookingPilot?.email ||
    selectedBooking?.user_id ||
    '';

  const officialEntry =
    techLogMap[String(selectedBooking.id)] ||
    {};

  return buildOutstandingPrefill({
    booking: selectedBooking,
    pilotName: bookingPilotName,
    baseAirfield,
    officialEntry,
  });
}, [selectedBooking, selectedBookingPilot, techLogMap, baseAirfield]);

const aircraftOptions = useMemo(() => {
  return Object.values(aircraftMap)
    .map((row) => ({
      id: String(row?.id || '').trim(),
      registration: String(row?.registration || '').trim(),
      icaoType: String(row?.icao_type || '').trim(),
    }))
    .filter((row) => row.id)
    .sort((a, b) =>
      a.registration.localeCompare(b.registration, undefined, {
        sensitivity: 'base',
      })
    );
}, [aircraftMap]);

function handleAircraftSelectChange(event) {
  const nextAircraftId = String(event.target.value || '').trim();
  const params = new URLSearchParams();

  if (nextAircraftId) {
    params.set('aircraftId', nextAircraftId);
  }

  if (fromQueryParam) {
    params.set('from', fromQueryParam);
  }

  const query = params.toString();
  navigate(query ? `/aircraft-tech-log?${query}` : '/aircraft-tech-log');
}

useEffect(() => {
  if (selectedBooking?.id) {
    setExpandedBookingId(selectedBooking.id);
  }
}, [selectedBooking?.id]);

const sheetBookings = useMemo(() => {
    if (!selectedAircraftId) return [];
    return visibleBookings.filter((booking) => {
      const key = String(booking.id);
      const entry = techLogMap[key];
      return !!entry && Object.keys(entry).length > 0;
    });
  }, [selectedAircraftId, visibleBookings, techLogMap]);

  const annualCountdownDays = useMemo(() => getAnnualCountdownDays(annualCheckDate), [annualCheckDate]);
  const paperLogKey = useMemo(() => getPaperLogKey(selectedAircraftId), [selectedAircraftId]);
  const selectedPaperLogEntry = useMemo(() => {
    if (!paperLogKey) return {};
    return techLogMap[paperLogKey] || {};
  }, [paperLogKey, techLogMap]);

  useEffect(() => {
    setPaperLogDraft(buildPaperLogDraft(selectedPaperLogEntry));
  }, [selectedAircraftId, selectedPaperLogEntry]);

  const centralLogRows = useMemo(() => {
    if (!selectedAircraftId) return [];

    const paperRow = {
      'Flight Number': paperLogDraft.flightNumber || '',
      Date: formatDateValue(paperLogDraft.flightDate),
      Name: 'Initial entry',
      'Dep Airfield / Arr Airfield': formatAirfields(paperLogDraft.departureAirfield, paperLogDraft.arrivalAirfield),
      'Dep Time': paperLogDraft.departureTime || '',
      'Arr Time': paperLogDraft.arrivalTime || '',
      'Flight Hours': paperLogDraft.flightTime || '',
      'Fuel Uplift': paperLogDraft.fuelUpliftLitres ?? '',
      'Oil uplift': paperLogDraft.oilUpliftLitres ?? '',
      Sig: paperLogDraft.signature || '',
      'Nxt Check': paperLogDraft.nextCheck || '',
      defects: paperLogDraft.defect || '',
      Rect: paperLogDraft.rectification || '',
      'Eng Sig': paperLogDraft.engineerSignature || '',
      Remarks: paperLogDraft.remarks || '',
    };

    const bookingRows = sheetBookings.map((booking, index) => {
      const bookingKey = String(booking.id);
      const pilot = userMap[String(booking.user_id)];
      const pilotName = pilot?.full_name || pilot?.email || booking.user_id;
      const aircraft = aircraftMap[String(booking.aircraft_id)];
      const entry = techLogMap[bookingKey] || {};
      const draft = spreadsheetDraftMap[bookingKey] || buildSpreadsheetDraft({
        booking,
        entry,
        aircraft,
        defaultMethod: appSettings?.techLog?.recordingMethod || 'hobbs',
        defaultTaxiTimeEnabled: appSettings?.techLog?.defaultTaxiTimeEnabled,
        defaultTaxiTimeMinutes: appSettings?.techLog?.defaultTaxiTimeMinutes,
        defaultTaxiTimeHours: appSettings?.techLog?.defaultTaxiTimeHours,
      });
      const nextCheckFallback = formatCheckCountdownHours(entry);
      const autoFlightNumber = getAutoIncrementFlightNumber(paperLogDraft.flightNumber, index + 1);

      return {
        'Flight Number': draft.flightNumber || autoFlightNumber || bookingKey,
        Date: formatDateValue(booking.date),
        Name: pilotName || '',
        'Dep Airfield / Arr Airfield': formatAirfields(draft.departureAirfield, draft.arrivalAirfield),
        'Dep Time': draft.departureTime || booking.time || '',
        'Arr Time': draft.arrivalTime || booking.end_time || '',
        'Flight Hours': draft.flightTime || '',
        'Fuel Uplift': draft.fuelUpliftLitres ?? '',
        'Oil uplift': draft.oilUpliftLitres ?? '',
        Sig: draft.signature || '',
        'Nxt Check': draft.nextCheck || nextCheckFallback,
        defects: draft.defect || '',
        Rect: draft.rectification || '',
        'Eng Sig': draft.engineerSignature || '',
        Remarks: draft.remarks || draft.notes || '',
      };
    });

    return [paperRow, ...bookingRows];
  }, [selectedAircraftId, sheetBookings, userMap, aircraftMap, techLogMap, spreadsheetDraftMap, appSettings?.techLog?.recordingMethod, appSettings?.techLog?.defaultTaxiTimeEnabled, appSettings?.techLog?.defaultTaxiTimeMinutes, appSettings?.techLog?.defaultTaxiTimeHours, paperLogDraft]);

  async function handleExportCentralTechLogXlsx() {
    if (centralLogRows.length === 0) {
      setError('No tech log rows available to export yet.');
      return;
    }

    const fileSuffix = selectedAircraft?.registration ? `-${selectedAircraft.registration}` : '';
    try {
      await downloadCentralTechLogXlsx(centralLogRows, `tech-log${fileSuffix}.xlsx`);
    } catch (error) {
      console.error('Could not export the central tech log.', error);
      setError('Could not export the Tech Log. Please try again.');
    }
  }

  useEffect(() => {
    if (!selectedAircraftId) {
      setAnnualCheckDate('');
      return;
    }

    setAnnualCheckDate(getAircraftAnnualCheckDate(selectedAircraftId));
  }, [selectedAircraftId]);

  useEffect(() => {
    if (!selectedAircraftId || loading) return;

    setSpreadsheetDraftMap((prev) => {
      const next = { ...prev };
      let changed = false;

      visibleBookings.forEach((booking) => {
        const bookingKey = String(booking.id);
        const derivedDraft = buildSpreadsheetDraft({
          booking,
          entry: techLogMap[bookingKey],
          aircraft: aircraftMap[String(booking.aircraft_id)],
          defaultMethod: appSettings?.techLog?.recordingMethod || 'hobbs',
          defaultTaxiTimeEnabled: appSettings?.techLog?.defaultTaxiTimeEnabled,
          defaultTaxiTimeMinutes: appSettings?.techLog?.defaultTaxiTimeMinutes,
          defaultTaxiTimeHours: appSettings?.techLog?.defaultTaxiTimeHours,
        });

        const existingDraft = next[bookingKey];
        if (!existingDraft) {
          next[bookingKey] = derivedDraft;
          changed = true;
          return;
        }

        const entry = techLogMap[bookingKey];
        const hasEntry = !!entry && Object.keys(entry).length > 0;
        if (!hasEntry) return;

        const mergedDraft = { ...existingDraft };
        let mergedChanged = false;

        Object.entries(derivedDraft).forEach(([field, value]) => {
          const current = mergedDraft[field];
          const currentEmpty = current === undefined || current === null || String(current).trim() === '';
          const valuePresent = value !== undefined && value !== null && String(value).trim() !== '';

          if (currentEmpty && valuePresent) {
            mergedDraft[field] = value;
            mergedChanged = true;
          }
        });

        if (mergedChanged) {
          next[bookingKey] = mergedDraft;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [selectedAircraftId, loading, visibleBookings, techLogMap, aircraftMap, appSettings?.techLog?.recordingMethod, appSettings?.techLog?.defaultTaxiTimeEnabled, appSettings?.techLog?.defaultTaxiTimeMinutes, appSettings?.techLog?.defaultTaxiTimeHours]);

  function updateSpreadsheetCell(bookingId, field, value) {
    const bookingKey = String(bookingId);
    setSpreadsheetDraftMap((prev) => ({
      ...prev,
      [bookingKey]: {
        ...(prev[bookingKey] || {}),
        [field]: value,
      },
    }));
  }

  async function handleSpreadsheetSave(booking) {
    const bookingKey = String(booking.id);
    const aircraft = aircraftMap[String(booking.aircraft_id)];
    const pilot = userMap[String(booking.user_id)];
    const pilotName = pilot?.full_name || pilot?.email || booking.user_id;
    const draft = spreadsheetDraftMap[bookingKey] || buildSpreadsheetDraft({
      booking,
      entry: techLogMap[bookingKey],
      aircraft,
      defaultMethod: appSettings?.techLog?.recordingMethod || 'hobbs',
      defaultTaxiTimeEnabled: appSettings?.techLog?.defaultTaxiTimeEnabled,
      defaultTaxiTimeMinutes: appSettings?.techLog?.defaultTaxiTimeMinutes,
      defaultTaxiTimeHours: appSettings?.techLog?.defaultTaxiTimeHours,
    });

    const selectedMethod = aircraft?.recording_method || appSettings?.techLog?.recordingMethod || 'hobbs';
    const nextCheckFallback = formatCheckCountdownHours(techLogMap[bookingKey] || {});
    const rowIndex = sheetBookings.findIndex((row) => String(row.id) === String(booking.id));
    const autoFlightNumber = getAutoIncrementFlightNumber(paperLogDraft.flightNumber, rowIndex >= 0 ? rowIndex + 1 : 0);

    const payload = {
      flightNumber: draft.flightNumber || autoFlightNumber || String(booking.id),
      departureAirfield: draft.departureAirfield || '',
      arrivalAirfield: draft.arrivalAirfield || '',
      departureTime: draft.departureTime || booking.time || '',
      arrivalTime: draft.arrivalTime || booking.end_time || '',
      recordingMethod: selectedMethod,
      methodStart: draft.methodStart || '',
      methodEnd: draft.methodEnd || '',
      hobbsStart: String(selectedMethod).toLowerCase() === 'hobbs' ? (draft.methodStart || '') : '',
      hobbsEnd: String(selectedMethod).toLowerCase() === 'hobbs' ? (draft.methodEnd || '') : '',
      flightTime: draft.flightTime || '',
      taxiTime: draft.taxiTime || '',
      defect: draft.defect || '',
      snag: draft.defect || '',
      rectification: draft.rectification || '',
      signature: draft.signature || '',
      engineerSignature: draft.engineerSignature || '',
      nextCheck: draft.nextCheck || nextCheckFallback,
      fuelUpliftLitres: firstMeaningfulValue(
        draft.fuelUpliftLitres,
        ''
      ),
      oilUpliftLitres: firstMeaningfulValue(
        draft.oilUpliftLitres,
        ''
      ),
      checkCompleted: !!draft.checkCompleted,
      checkType: draft.checkType || '',
      checkCompletedAt: draft.checkCompleted ? (draft.checkCompletedAt || draft.methodEnd || '') : '',
      flightDate: booking.date || '',
      pilotName,
      notes: draft.remarks ?? '',
remarks: draft.remarks ?? '',
    };

    await handleTechLogSave(booking.id, payload);
  }

  async function handlePaperLogSave() {
    if (!paperLogKey) {
      setError('Select an aircraft before saving paper tech log details.');
      return;
    }

    const selectedMethod = selectedAircraft?.recording_method || appSettings?.techLog?.recordingMethod || 'hobbs';
    const payload = {
      ...paperLogDraft,
      flightDate: paperLogDraft.flightDate || '',
      recordingMethod: selectedMethod,
      methodStart: paperLogDraft.departureTime || '',
      methodEnd: paperLogDraft.arrivalTime || '',
      hobbsStart: String(selectedMethod).toLowerCase() === 'hobbs' ? (paperLogDraft.departureTime || '') : '',
      hobbsEnd: String(selectedMethod).toLowerCase() === 'hobbs' ? (paperLogDraft.arrivalTime || '') : '',
      snag: paperLogDraft.defect || '',
      notes: paperLogDraft.remarks || '',
      remarks: paperLogDraft.remarks || '',
      paperLogSeed: true,
      paperLogSavedAt: new Date().toISOString(),
    };

    await handleTechLogSave(paperLogKey, payload);
    setError('');
  }

  function handleAnnualCheckDateChange(value) {
    setAnnualCheckDate(value);
    if (selectedAircraftId) {
      setAircraftAnnualCheckDate(selectedAircraftId, value);
    }
  }

  if (!roleResolved) {
    return (
      <div style={{ color: '#b6eaff', padding: '2rem' }}>
        Checking Tech Log access...
      </div>
    );
  }

  if (!loading && !isOwner) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div
      style={{
        maxWidth: 1700,
        margin: '1.25rem auto',
        padding: '0 1rem 1.5rem',
        color: '#f3f8ff',
      }}
    >
      <div
        style={{
          background: '#0d1b2e',
          border: '1px solid #294867',
          borderRadius: 14,
          padding: '0.9rem 1rem',
          marginBottom: '0.9rem',
          display: 'grid',
          gap: '0.8rem',
          boxShadow: '0 12px 30px rgba(0,0,0,0.18)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.8rem',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div
              style={{
                color: '#55c7ff',
                fontSize: '0.66rem',
                fontWeight: 850,
                letterSpacing: '0.11em',
                textTransform: 'uppercase',
                marginBottom: '0.2rem',
              }}
            >
              Aircraft Tech Log
            </div>

            <h2
              style={{
                color: '#f3f8ff',
                fontWeight: 800,
                fontSize: '1.3rem',
                margin: 0,
                letterSpacing: '-0.02em',
              }}
            >
              {selectedAircraft
                ? selectedAircraft.icao_type
                  ? `${selectedAircraft.registration} (${selectedAircraft.icao_type})`
                  : selectedAircraft.registration
                : 'Select an aircraft'}
            </h2>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.55rem',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={() =>
                navigate(
                  sourcePage === 'admin'
                    ? '/admin'
                    : '/dashboard'
                )
              }
              style={{
                ...buttonStyle,
                background: '#12253b',
                color: '#dceeff',
                border: '1px solid #365a7c',
                borderRadius: 8,
                padding: '0.48rem 0.85rem',
                fontSize: '0.84rem',
                fontWeight: 750,
                boxShadow: 'none',
              }}
            >
              {sourcePage === 'admin'
                ? 'Back to Admin'
                : 'Back to Dashboard'}
            </button>

            {selectedAircraftId ? (
              <button
                type="button"
                onClick={handleExportCentralTechLogXlsx}
                style={{
                  ...buttonStyle,
                  background: '#12253b',
                  color: '#dceeff',
                  border: '1px solid #365a7c',
                  borderRadius: 8,
                  padding: '0.48rem 0.85rem',
                  fontSize: '0.84rem',
                  fontWeight: 750,
                  boxShadow: 'none',
                }}
              >
                Export XLSX
              </button>
            ) : null}
          </div>
        </div>

        {!loading &&
        !error &&
        aircraftOptions.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gap: '0.3rem',
              width: 'min(100%, 360px)',
            }}
          >
            <label
              htmlFor="aircraft-tech-log-selector"
              style={{
                color: '#bcd4e8',
                fontSize: '0.74rem',
                fontWeight: 800,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              Aircraft
            </label>

            <select
              id="aircraft-tech-log-selector"
              value={selectedAircraftId}
              onChange={handleAircraftSelectChange}
              style={{
                width: '100%',
                background: '#10243a',
                color: '#f3f8ff',
                border: '1px solid #3f6b91',
                borderRadius: 8,
                padding: '0.55rem 0.7rem',
                fontSize: '0.9rem',
              }}
            >
              <option value="">
                Select an aircraft
              </option>

              {aircraftOptions.map((aircraft) => {
                const label = aircraft.icaoType
                  ? `${aircraft.registration} (${aircraft.icaoType})`
                  : aircraft.registration;

                return (
                  <option
                    key={aircraft.id}
                    value={aircraft.id}
                  >
                    {label}
                  </option>
                );
              })}
            </select>
          </div>
        ) : null}
      </div>
      {loading ? <div>Loading tech log records...</div> : null}
      {error ? <div style={{ color: '#ff8d8d', marginBottom: '0.8rem' }}>{error}</div> : null}

      {!loading && !error && !selectedAircraftId && visibleBookings.length === 0 ? (
        <div style={{ color: '#374151', fontSize: '0.9rem', marginTop: '0.25rem' }}>
          Select an aircraft to view its technical log.
        </div>
      ) : null}

      {!loading && !error && selectedAircraftId && (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: '0.8rem',
      width: '100%',
    }}
  >
    {selectedBooking ? (
      <div
        style={{
          background: "#10243a",
          border: "1px solid #365a7c",
          borderRadius: 10,
          padding: "0.75rem 0.85rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "0.8rem",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              color: "#55c7ff",
              fontSize: "0.72rem",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            Selected outstanding entry
          </div>

          <div
            style={{
              color: "#f3f8ff",
fontSize: "0.95rem",
fontWeight: 700,
lineHeight: 1.3,
marginTop: "0.2rem",
            }}
          >
            Booking #{selectedBooking.id} ·{" "}
            {selectedBookingPilot?.full_name ||
              selectedBookingPilot?.email ||
              selectedBooking.user_id}
          </div>

          <div
            style={{
              color: "#bcd4e8",
              fontSize: "0.8rem",
              marginTop: "0.2rem",
            }}
          >
            {selectedBookingPrefill.departureAirfield || "—"}
            {" → "}
            {selectedBookingPrefill.arrivalAirfield || "—"}
            {" · "}
            {selectedBookingPrefill.departureTime || "--:--"}
            {" – "}
            {selectedBookingPrefill.arrivalTime || "--:--"}
          </div>
        </div>

        <div
          style={{
            color:
              Object.keys(selectedBookingEntry).length > 0
                ? "#9ef4bf"
                : "#ffd28a",
            fontSize: "0.8rem",
            fontWeight: 750,
          }}
        >
          {Object.keys(selectedBookingEntry).length > 0
            ? "Edit the official entry in the row below, then press Save."
            : "Waiting for the pilot Post-Flight entry."}
        </div>
      </div>
    ) : null}
    {selectedBookingNeedsOnBehalfCompletion ? (
      <div
        style={{
          background: 'rgba(242,184,75,0.08)',
          border:
            '1px solid rgba(242,184,75,0.42)',
          borderRadius: 10,
          padding: '0.85rem',
        }}
      >
        <div
          style={{
            color: '#f2b84b',
            fontSize: '0.86rem',
            fontWeight: 800,
          }}
        >
          Post-Flight not completed
        </div>

        <div
          style={{
            color: '#dceeff',
            fontSize: '0.8rem',
            lineHeight: 1.45,
            marginTop: '0.3rem',
          }}
        >
          Confirm the flight details with the pilot,
          enter the reason below, then complete the
          technical log on their behalf.
        </div>

        <label
          style={{
            display: 'grid',
            gap: '0.35rem',
            marginTop: '0.75rem',
            color: '#bcd4e8',
            fontSize: '0.78rem',
            fontWeight: 700,
          }}
        >
          <span>
            Reason for completing on behalf
          </span>

          <textarea
            rows={2}
            value={onBehalfReason}
            onChange={(event) =>
              setOnBehalfReason(
                event.target.value
              )
            }
            placeholder="For example: pilot unavailable; details confirmed by telephone."
            style={{
              width: '100%',
              boxSizing: 'border-box',
              resize: 'vertical',
              borderRadius: 7,
              border: '1px solid #496987',
              background: '#081727',
              color: '#f3f8ff',
              padding: '0.6rem',
              fontFamily: 'inherit',
              fontSize: '0.82rem',
            }}
          />
        </label>

        <div style={{ marginTop: '0.8rem' }}>
          <AircraftTechLog
            prefill={selectedBookingPrefill}
            options={{
              ...appSettings.techLog,

              recordingMethod:
                selectedAircraft
                  ?.recording_method ||
                appSettings?.techLog
                  ?.recordingMethod ||
                'hobbs',

              checkIntervalHours:
                appSettings.maintenance
                  ?.checkIntervalHours,
            }}
            onSave={handleCompleteOnBehalf}
          />
        </div>

        {onBehalfSaving ? (
          <div
            style={{
              color: '#bcd4e8',
              fontSize: '0.78rem',
              marginTop: '0.5rem',
            }}
          >
            Saving entry...
          </div>
        ) : null}

        {saveNoticeMap[
          `${selectedBooking.id}:tech`
        ] ? (
          <div
            style={{
              color: '#f3f8ff',
              fontSize: '0.78rem',
              marginTop: '0.5rem',
            }}
          >
            {
              saveNoticeMap[
                `${selectedBooking.id}:tech`
              ]
            }
          </div>
        ) : null}
      </div>
    ) : null}
    <div
      style={{
        color: '#f3f8ff',
        fontWeight: 700,
        fontSize: '1.15rem',
        margin: '0.35rem 0 0.65rem',
        textAlign: 'left',
      }}
    >
      Aircraft Technical Log
    </div>

          <div style={{ background: '#f8fafc', border: '1px solid #c8ced6', borderRadius: 6, padding: '0.7rem 0.8rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'flex-start', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
              <span style={{ color: '#111111', fontWeight: 700, fontSize: '0.9rem' }}>Next Annual Check:</span>
              <input
                type="date"
                value={annualCheckDate}
                onChange={(e) => handleAnnualCheckDateChange(e.target.value)}
                style={{ ...spreadsheetInputStyle, width: 155, fontSize: '0.85rem', padding: '0.4rem 0.5rem' }}
              />
            </div>
            <div style={{ color: '#333333', fontWeight: 700, fontSize: '0.85rem', maxWidth: '100%', wordBreak: 'break-word' }}>
              {annualCountdownDays == null ? (
                'Set annual date'
              ) : (
                <span style={getAnnualDueStyle(annualCountdownDays)}>
                  {annualCountdownDays < 0
                    ? `${Math.abs(annualCountdownDays)} days overdue`
                    : `${annualCountdownDays} days remaining`}
                </span>
              )}
            </div>
          </div>

          {isPhone ? (
            <MobileAircraftTechLog
              aircraft={selectedAircraft}
              bookings={sheetBookings}
              userMap={userMap}
              techLogMap={techLogMap}
              expandedBookingId={expandedBookingId}
              toggleBooking={(bookingId) => setExpandedBookingId(
                expandedBookingId === bookingId ? null : bookingId
              )}
              renderEditor={(booking) => {
                const bookingKey = String(booking.id);
                const pilot = userMap[String(booking.user_id)];
                const pilotName = pilot?.full_name || pilot?.email || booking.user_id;
                return (
                  <AircraftTechLog
                    prefill={{
                      ...sanitizeTechLogPrefill(techLogMap[bookingKey] || {}),
                      bookingId: bookingKey,
                      flightNumber: bookingKey,
                      flightDate: booking.date || '',
                      pilotName,
                    }}
                    options={{
                      ...appSettings.techLog,
                      recordingMethod: selectedAircraft?.recording_method || appSettings?.techLog?.recordingMethod || 'hobbs',
                      checkIntervalHours: appSettings.maintenance?.checkIntervalHours,
                    }}
                    onSave={(entry) => handleTechLogSave(booking.id, entry)}
                  />
                );
              }}
              paperDraft={paperLogDraft}
              setPaperDraft={setPaperLogDraft}
              savePaperDraft={handlePaperLogSave}
              paperNotice={saveNoticeMap[`${paperLogKey}:tech`] || (techLogMap[paperLogKey] ? 'Ready' : 'Pending')}
            />
          ) : null}

          <div className="aircraft-tech-log-desktop-sheet" style={{ background: '#ffffff', border: '1px solid #111111', borderRadius: 0, padding: '0.5rem', minWidth: 0 }}>
          <div
            style={{
              overflowX: useMobileTableScroll
                ? 'auto'
                : 'hidden',
            }}
          >
            <div
              style={{
                minWidth: 1920,
                width: 1920,
                zoom: useMobileTableScroll
                  ? 1
                  : desktopSheetScale,
              }}
            >
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1920, tableLayout: 'fixed', fontFamily: 'Arial, Helvetica, sans-serif' }}>
              <colgroup>
                <col style={{ width: 78 }} />
                <col style={{ width: 82 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 220 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 95 }} />
                <col style={{ width: 95 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 95 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 145 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 220 }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={spreadsheetHeaderCellStyle}>Flight Number</th>
                  <th style={spreadsheetHeaderCellStyle}>Date</th>
                  <th style={spreadsheetHeaderCellStyle}>Name</th>
                  <th style={spreadsheetHeaderCellStyle}>Dep Airfield / Arr Airfield</th>
                  <th style={spreadsheetHeaderCellStyle}>Dep Time</th>
                  <th style={spreadsheetHeaderCellStyle}>Arr Time</th>
                  <th style={spreadsheetHeaderCellStyle}>Flight Hrs</th>
                  <th style={spreadsheetHeaderCellStyle}>Fuel Uplift</th>
                  <th style={spreadsheetHeaderCellStyle}>Oil uplift</th>
                  <th style={spreadsheetHeaderCellStyle}>Sig</th>
                  <th style={spreadsheetHeaderCellStyle}>Nxt Check</th>
                  <th style={spreadsheetHeaderCellStyle}>Defects</th>
                  <th style={spreadsheetHeaderCellStyle}>Rect</th>
                  <th style={spreadsheetHeaderCellStyle}>Eng Sig</th>
                  <th style={spreadsheetHeaderCellStyle}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={spreadsheetCellStyle}>
                    <input
                      value={paperLogDraft.flightNumber || ''}
                      onChange={(e) => setPaperLogDraft((prev) => ({ ...prev, flightNumber: String(e.target.value || '').replace(/\D/g, '') }))}
                      style={spreadsheetInputStyle}
                      placeholder="0001"
                    />
                  </td>
                  <td style={spreadsheetCellStyle}>
                    <input
                      value={paperLogDraft.flightDate || ''}
                      onChange={(e) => setPaperLogDraft((prev) => ({ ...prev, flightDate: e.target.value }))}
                      style={{ ...spreadsheetInputStyle, fontSize: '0.76rem', padding: '0.18rem 0.1rem' }}
                      placeholder="DD/MM/YY"
                    />
                  </td>
                  <td style={spreadsheetCellStyle}>Initial entry</td>
                  <td style={spreadsheetCellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input
                        value={paperLogDraft.departureAirfield || ''}
                        onChange={(e) => setPaperLogDraft((prev) => ({ ...prev, departureAirfield: e.target.value.toUpperCase() }))}
                        style={spreadsheetInputStyle}
                        placeholder="DEP"
                      />
                      <input
                        value={paperLogDraft.arrivalAirfield || ''}
                        onChange={(e) => setPaperLogDraft((prev) => ({ ...prev, arrivalAirfield: e.target.value.toUpperCase() }))}
                        style={spreadsheetInputStyle}
                        placeholder="ARR"
                      />
                    </div>
                  </td>
                  <td style={spreadsheetCellStyle}>
                    <input value={paperLogDraft.departureTime || ''} onChange={(e) => setPaperLogDraft((prev) => ({ ...prev, departureTime: e.target.value }))} style={spreadsheetInputStyle} placeholder="HH:MM" />
                  </td>
                  <td style={spreadsheetCellStyle}>
                    <input value={paperLogDraft.arrivalTime || ''} onChange={(e) => setPaperLogDraft((prev) => ({ ...prev, arrivalTime: e.target.value }))} style={spreadsheetInputStyle} placeholder="HH:MM" />
                  </td>
                  <td style={spreadsheetCellStyle}>
                    <input value={paperLogDraft.flightTime || ''} onChange={(e) => setPaperLogDraft((prev) => ({ ...prev, flightTime: e.target.value }))} style={spreadsheetInputStyle} placeholder="e.g. 1.2" />
                  </td>
                  <td style={spreadsheetCellStyle}>
                    <input value={paperLogDraft.fuelUpliftLitres ?? ''} onChange={(e) => setPaperLogDraft((prev) => ({ ...prev, fuelUpliftLitres: e.target.value }))} style={spreadsheetInputStyle} placeholder="0" />
                  </td>
                  <td style={spreadsheetCellStyle}>
                    <input value={paperLogDraft.oilUpliftLitres ?? ''} onChange={(e) => setPaperLogDraft((prev) => ({ ...prev, oilUpliftLitres: e.target.value }))} style={spreadsheetInputStyle} placeholder="0" />
                  </td>
                  <td style={spreadsheetCellStyle}>
                    <input value={paperLogDraft.signature || ''} onChange={(e) => setPaperLogDraft((prev) => ({ ...prev, signature: e.target.value }))} style={spreadsheetInputStyle} />
                  </td>
                  <td style={spreadsheetCellStyle}>
                    <input value={paperLogDraft.nextCheck || ''} onChange={(e) => setPaperLogDraft((prev) => ({ ...prev, nextCheck: e.target.value }))} style={spreadsheetInputStyle} />
                  </td>
                  <td style={spreadsheetCellStyle}>
                    <input value={paperLogDraft.defect || ''} onChange={(e) => setPaperLogDraft((prev) => ({ ...prev, defect: e.target.value }))} style={spreadsheetInputStyle} />
                  </td>
                  <td style={spreadsheetCellStyle}>
                    <input value={paperLogDraft.rectification || ''} onChange={(e) => setPaperLogDraft((prev) => ({ ...prev, rectification: e.target.value }))} style={spreadsheetInputStyle} />
                  </td>
                  <td style={spreadsheetCellStyle}>
                    <input value={paperLogDraft.engineerSignature || ''} onChange={(e) => setPaperLogDraft((prev) => ({ ...prev, engineerSignature: e.target.value }))} style={spreadsheetInputStyle} />
                  </td>
                  <td style={spreadsheetCellStyle}>
                    <input value={paperLogDraft.remarks || ''} onChange={(e) => setPaperLogDraft((prev) => ({ ...prev, remarks: e.target.value }))} style={spreadsheetInputStyle} />
                  </td>
                </tr>
                <tr>
                  <td
                    colSpan={15}
                    style={{
                      ...spreadsheetCellStyle,
                      padding: '0.3rem 0.4rem',
                      background: '#f8fafc',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.6rem', width: '100%', boxSizing: 'border-box', paddingRight: '2rem', minWidth: 0 }}>
                      <span style={{ color: saveNoticeMap[`${paperLogKey}:tech`] ? (saveNoticeMap[`${paperLogKey}:tech`].toLowerCase().includes('saved') ? '#1a7f37' : '#9a3412') : '#6b7280', fontSize: '0.72rem', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                        {saveNoticeMap[`${paperLogKey}:tech`] || (techLogMap[paperLogKey] ? 'Ready' : 'Pending')}
                      </span>
                      <button
                        type="button"
                        onClick={handlePaperLogSave}
                        style={{ ...buttonStyle, padding: '0.22rem 0.55rem', fontSize: '0.72rem', background: '#ffffff', color: '#111111', border: '1px solid #9aa5b1', boxShadow: 'none', whiteSpace: 'nowrap' }}
                      >
                        Save
                      </button>
                    </div>
                  </td>
                </tr>

                {sheetBookings.map((booking, index) => {
                  const bookingKey = String(booking.id);
                  const pilot = userMap[String(booking.user_id)];
                  const pilotName = pilot?.full_name || pilot?.email || booking.user_id;
                  const aircraft = aircraftMap[String(booking.aircraft_id)];
                  const entry = techLogMap[bookingKey] || {};
                  const draft = spreadsheetDraftMap[bookingKey] || buildSpreadsheetDraft({
                    booking,
                    entry,
                    aircraft,
                    defaultMethod: appSettings?.techLog?.recordingMethod || 'hobbs',
                    defaultTaxiTimeEnabled: appSettings?.techLog?.defaultTaxiTimeEnabled,
                    defaultTaxiTimeMinutes: appSettings?.techLog?.defaultTaxiTimeMinutes,
                    defaultTaxiTimeHours: appSettings?.techLog?.defaultTaxiTimeHours,
                  });
                  const nextCheckFallback = formatCheckCountdownHours(entry);
                  const autoFlightNumber = getAutoIncrementFlightNumber(paperLogDraft.flightNumber, index + 1);
                  const notice = saveNoticeMap[`${booking.id}:tech`] || '';
                  return (
                    <React.Fragment key={booking.id}>
                      <tr>
                        <td style={spreadsheetCellStyle}>{draft.flightNumber || autoFlightNumber || booking.id}</td>
                        <td style={spreadsheetCellStyle}>{formatDateValue(booking.date) || '-'}</td>
                        <td style={spreadsheetCellStyle}>{pilotName}</td>
                        <td style={spreadsheetCellStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input
                              value={draft.departureAirfield || ''}
                              onChange={(e) => updateSpreadsheetCell(booking.id, 'departureAirfield', e.target.value.toUpperCase())}
                              style={spreadsheetInputStyle}
                              placeholder="DEP"
                            />
                            <input
                              value={draft.arrivalAirfield || ''}
                              onChange={(e) => updateSpreadsheetCell(booking.id, 'arrivalAirfield', e.target.value.toUpperCase())}
                              style={spreadsheetInputStyle}
                              placeholder="ARR"
                            />
                          </div>
                        </td>
                        <td style={spreadsheetCellStyle}>
                          <input value={draft.departureTime || ''} onChange={(e) => updateSpreadsheetCell(booking.id, 'departureTime', e.target.value)} style={spreadsheetInputStyle} placeholder="HH:MM" />
                        </td>
                        <td style={spreadsheetCellStyle}>
                          <input value={draft.arrivalTime || ''} onChange={(e) => updateSpreadsheetCell(booking.id, 'arrivalTime', e.target.value)} style={spreadsheetInputStyle} placeholder="HH:MM" />
                        </td>
                        <td style={spreadsheetCellStyle}>
                          <input value={draft.flightTime || ''} onChange={(e) => updateSpreadsheetCell(booking.id, 'flightTime', e.target.value)} style={spreadsheetInputStyle} placeholder="e.g. 1.2" />
                        </td>
                        <td style={spreadsheetCellStyle}>
                          <input value={draft.fuelUpliftLitres ?? ''} onChange={(e) => updateSpreadsheetCell(booking.id, 'fuelUpliftLitres', e.target.value)} style={spreadsheetInputStyle} placeholder="0" />
                        </td>
                        <td style={spreadsheetCellStyle}>
                          <input value={draft.oilUpliftLitres ?? ''} onChange={(e) => updateSpreadsheetCell(booking.id, 'oilUpliftLitres', e.target.value)} style={spreadsheetInputStyle} placeholder="0" />
                        </td>
                        <td style={spreadsheetCellStyle}>
                          <input value={draft.signature || ''} onChange={(e) => updateSpreadsheetCell(booking.id, 'signature', e.target.value)} style={spreadsheetInputStyle} />
                        </td>
                        <td style={spreadsheetCellStyle}>
                          <input value={draft.nextCheck || nextCheckFallback} onChange={(e) => updateSpreadsheetCell(booking.id, 'nextCheck', e.target.value)} style={spreadsheetInputStyle} />
                        </td>
                        <td style={spreadsheetCellStyle}>
                          <input value={draft.defect || ''} onChange={(e) => updateSpreadsheetCell(booking.id, 'defect', e.target.value)} style={spreadsheetInputStyle} />
                        </td>
                        <td style={spreadsheetCellStyle}>
                          <input value={draft.rectification || ''} onChange={(e) => updateSpreadsheetCell(booking.id, 'rectification', e.target.value)} style={spreadsheetInputStyle} />
                        </td>
                        <td style={spreadsheetCellStyle}>
                          <input value={draft.engineerSignature || ''} onChange={(e) => updateSpreadsheetCell(booking.id, 'engineerSignature', e.target.value)} style={spreadsheetInputStyle} />
                        </td>
                        <td style={spreadsheetCellStyle}>
                          <input value={draft.remarks || ''} onChange={(e) => updateSpreadsheetCell(booking.id, 'remarks', e.target.value)} style={spreadsheetInputStyle} />
                        </td>
                      </tr>
                      <tr>
                        <td
                          colSpan={15}
                          style={{
                            ...spreadsheetCellStyle,
                            padding: '0.3rem 0.4rem',
                            background: '#f8fafc',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.6rem', width: '100%', boxSizing: 'border-box', paddingRight: '2rem', minWidth: 0 }}>
                            <span style={{ color: notice ? (notice.toLowerCase().includes('saved') ? '#1a7f37' : '#9a3412') : '#6b7280', fontSize: '0.72rem', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                              {notice || (techLogMap[bookingKey] ? 'Ready' : 'Pending')}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleSpreadsheetSave(booking)}
                              style={{ ...buttonStyle, padding: '0.22rem 0.55rem', fontSize: '0.72rem', background: '#ffffff', color: '#111111', border: '1px solid #9aa5b1', boxShadow: 'none', whiteSpace: 'nowrap' }}
                            >
                              Save
                            </button>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
          {sheetBookings.length === 0 && (
            <div style={{ color: '#374151', fontSize: '0.85rem', lineHeight: 1.4, marginTop: '0.55rem', textAlign: 'left' }}>
              No completed flight entries yet. The first row can be used to enter the aircraft's opening technical-log details.
            </div>
          )}
          </div>
        </div>
      )}

      {!loading && !error && !selectedAircraftId && (
        <div style={{ display: 'grid', gap: '0.9rem' }}>
          {bookings.map((booking) => {
            const bookingKey = String(booking.id);
            const aircraft = aircraftMap[String(booking.aircraft_id)];
            const pilot = userMap[String(booking.user_id)];
            const pilotName = pilot?.full_name || pilot?.email || booking.user_id;
            const entry = techLogMap[bookingKey] || {};
            const hasEntry = !!techLogMap[bookingKey] && Object.keys(techLogMap[bookingKey]).length > 0;
            const start = parseDateTime(booking.date, booking.time);

            return (
              <div key={booking.id} style={{ background: '#23243a', borderRadius: 10, padding: '1rem', border: '1px solid #2f466d' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.8rem', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ color: '#1ecfff', fontWeight: 700 }}>
                      {aircraft ? `${aircraft.registration} (${aircraft.icao_type || ''})` : `Aircraft ${booking.aircraft_id}`}
                    </div>
                    <div style={{ color: '#b6eaff', marginTop: 2 }}>
                      Pilot: {pilotName}
                    </div>
                    <div style={{ color: '#8db7e6', marginTop: 2 }}>
                      Flight: {start ? `${formatLocalDate(start)} ${booking.time}` : `${formatDateValue(booking.date) || ''} ${booking.time || ''}`}
                    </div>
                    {hasEntry && (
                      <div style={{ color: '#9fdcff', marginTop: 6, display: 'grid', gap: 2 }}>
                        <div><strong>Defects:</strong> {entry.defect || entry.snag || '-'}</div>
                        <div><strong>Fuel/Oil Uplift:</strong> {entry.fuelUpliftLitres || '0'}L / {entry.oilUpliftLitres || '0'}L</div>
                        <div>
                          <strong>50hr Check Due:</strong>{' '}
                          <span style={getFiftyHourDueStyle(Number(entry.checkCountdownHours), 12)}>
                            {entry.checkCountdownHours ? `${entry.checkCountdownHours}h remaining` : '-'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: hasEntry ? '#7df0a8' : '#ffcc66', fontWeight: 700 }}>
                      {hasEntry ? 'Log updated' : 'Log pending'}
                    </div>
                    <button
                      type="button"
                      onClick={() => setExpandedBookingId(expandedBookingId === booking.id ? null : booking.id)}
                      style={{ ...buttonStyle, marginTop: 8, background: '#b6eaff', color: '#181a2a' }}
                    >
                      {expandedBookingId === booking.id ? 'Hide Entry' : 'View / Edit Entry'}
                    </button>
                  </div>
                </div>

                {expandedBookingId === booking.id && (
                  <div style={{ marginTop: '0.9rem', borderTop: '1px solid #35507f', paddingTop: '0.9rem' }}>
                    <AircraftTechLog
                      prefill={{
                        ...sanitizeTechLogPrefill(techLogMap[bookingKey] || {}),
                        bookingId: bookingKey,
                        flightNumber: bookingKey,
                        flightDate: booking.date || '',
                        pilotName,
                      }}
                      options={{
                        ...appSettings.techLog,
                        recordingMethod: aircraft?.recording_method || appSettings?.techLog?.recordingMethod || 'hobbs',
                        checkIntervalHours: appSettings.maintenance?.checkIntervalHours,
                      }}
                      onSave={(entry) => handleTechLogSave(booking.id, entry)}
                    />
                    {!!saveNoticeMap[`${booking.id}:tech`] && (
                      <div style={{ marginTop: '-1.25rem', marginBottom: '0.5rem', color: saveNoticeMap[`${booking.id}:tech`].toLowerCase().includes('saved') ? '#c8ffe5' : '#ffdfb8', background: saveNoticeMap[`${booking.id}:tech`].toLowerCase().includes('saved') ? '#0f3d2e' : '#43260f', border: saveNoticeMap[`${booking.id}:tech`].toLowerCase().includes('saved') ? '1px solid #2c7f63' : '1px solid #9a5b2f', borderRadius: 8, padding: '0.45rem 0.65rem' }}>
                        {saveNoticeMap[`${booking.id}:tech`]}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
